import React, { useCallback, useEffect, useRef, useState } from 'react';

// Internal Imports
import DataGrid from '../../common/components/DataGrid/DataGrid';
import MenuLayout from '../../common/components/MenuLayout/MenuLayout';
import ConfirmationModal from '../../common/components/Modal/CorfimationModal';
import Modal from '../../common/components/Modal/Modal';
import Paint from '../../common/components/Paint/Paint';
import { COLORS } from '../../common/constants/colors';
import { getError, openNotification, tryCatch } from '../../common/constants/Utilities';
import { Mode } from '../../common/types/AllTypes';
import ViewContainer from '../../common/components/ViewContainer/ViewContainer';
import { useAuth } from '../../contexts/auth';
import { useListDepartmentByUer } from '../../hooks/useListDepartment';
import { useListUsersByUser } from '../../hooks/useListUsers';
import IColumn from '../../interfaces/IColumn';
import { IMenu } from '../../interfaces/IMenu';
import { IUserGroup, IUserGroupDetail } from '../../interfaces/IUser';
import { DeactivateUserGroupByID, GetUserGroupByID, GetUserGroupByUserID } from '../../services/userGroup';
import AddUpdateUserGroup from './AddUpdateUserGroup';
import './GrupoUsuarios.css';
import { LoadingIcon } from '../../common/constants/cssElements';

// Interfaces
interface Props {
    menu: IMenu
}

const GrupoUsuarios: React.FC<Props> = ({ menu }) => {

    // States
    const { user } = useAuth();
    const { listDepartment, loadingDeparment } = useListDepartmentByUer();
    const { listUser, loadingUser } = useListUsersByUser();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingAction, setLoadingAction] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [mode, setMode] = useState<Mode>('Add');

    const [listUserGroup, setListUserGroup] = useState<IUserGroup[]>([]);
    const [userGroupUpdate, setUserGroupUpdate] = useState<IUserGroup>();
    const groupDep = useRef<IUserGroup>();
    const [searchV, setSearchV] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);


    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [idDetail, setIdDetail] = useState<number | null>(null);

    // Consts
    const columns: IColumn[] = [
        {
            title: 'Nombre',
            dataIndex: 'nombre',
        },
        {
            title: 'Departamento',
            dataIndex: 'departamentoNombre',
        },
        {
            title: <i className="fa-solid fa-rotate button_refresh" onClick={() => { handleGetUserGroupsByUserID(); setSearchV('') }}></i>,
            dataIndex: '',
            render: (userG: IUserGroup) => loadingDetail && idDetail === userG.grupoUsuarioID ? <LoadingIcon color={COLORS.blue_active} size={'20px'} /> : <></>
        }
    ];


    // handles
    const handleGetUserGroupsByUserID = () => {
        tryCatch(() => {
            if (!!listDepartment) {
                setLoading(true);
                GetUserGroupByUserID()
                    .then((response => {
                        const listGroup: IUserGroup[] = response.data;
                        listGroup.forEach(qtGroup => {
                            qtGroup.departamentoNombre = listDepartment.find(dep => dep.departamentoGUID === qtGroup.departamentoGUID)?.nombre;
                        })
                        setListUserGroup(listGroup);


                    }))
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoading(false));
            }
        })
    }


    const handleGetUserGroupByID = (userGroup: IUserGroup) => {
        setUserGroupUpdate(undefined);
        groupDep.current = undefined;
        if (!!userGroup) {

            setIdDetail(userGroup.grupoUsuarioID!);
            setLoadingDetail(true);

            tryCatch(() => {
                GetUserGroupByID(userGroup.grupoUsuarioID!)
                    .then(({ data }) => {
                        setUserGroupUpdate(data);
                        groupDep.current = userGroup;
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoadingDetail(false))
            })
        }
    }


    const handleDeactiveQuestionGruop = () => {
        tryCatch(() => {
            if (!!userGroupUpdate) {
                setLoadingAction(true);
                DeactivateUserGroupByID(userGroupUpdate.grupoUsuarioID!)
                    .then((response) => {
                        openNotification('success', `Grupo de Usuarios (${userGroupUpdate.nombre}) eliminado`, response.msg!);
                        setListUserGroup(listUserGroup.filter(userG => userG.grupoUsuarioID !== userGroupUpdate.grupoUsuarioID!));
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => {
                        setLoadingAction(false);
                        setDeleteModal(false);
                        setUserGroupUpdate(undefined);
                    })
            }
        })
    }

    // Methods
    const openModalUpdate = () => {
        if (!!!groupDep.current?.departamentoNombre) {
            openNotification('warning', `Este Grupo tiene el Departamento Deshabilitado`, 'Contáctese con el equipo Bis para más información.');
        } else {
            setMode('Update');
            setAddUpdateModal(true);
        }
    }

    const getUserGroupsFiltered = useCallback((): IUserGroup[] => {
        let userGroupsFiltered: IUserGroup[] = [];
        const valueLowerCase = searchV.toLowerCase();

        if (!!valueLowerCase) {
            userGroupsFiltered = listUserGroup.filter((Qtgroup) => Qtgroup.departamentoNombre?.toLowerCase().includes(valueLowerCase) || Qtgroup.nombre!.toLowerCase().includes(valueLowerCase));
        } else {
            userGroupsFiltered = listUserGroup;
        }

        return userGroupsFiltered;
    }, [searchV, listUserGroup]);

    //Effects
    useEffect(() => {
        handleGetUserGroupsByUserID()
    }, [, listDepartment]);

    return (
        <MenuLayout currentPage={menu.nombre}>
            <ViewContainer>
                <DataGrid
                    searchV={searchV}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    gridProps={{ rowKey: (record: any) => record.grupoUsuarioID }}
                    columns={columns}
                    searchMethod={setSearchV}
                    menu={menu}
                    loading={loading}
                    dataSource={getUserGroupsFiltered()}
                    setItemSelected={handleGetUserGroupByID}
                    addAction={{ active: true, method: () => { setMode('Add'); setAddUpdateModal(true) } }}
                    deleteAction={{ active: !!userGroupUpdate, method: () => setDeleteModal(true) }}
                    updateAction={{ active: !!userGroupUpdate, method: openModalUpdate }}
                />
            </ViewContainer>


            <ConfirmationModal
                title={menu.nombre}
                visible={deleteModal}
                setVisible={setDeleteModal}
                question="¿Seguro que deseas eliminar este Grupo de Usuarios?"
                method={handleDeactiveQuestionGruop}
                loading={loadingAction}
            />

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {mode === 'Add' ? 'Crear' : 'Modificar'}  <Paint color={COLORS.blue_main(1)}>Grupo de Usuarios</Paint></nav>} visible={addUpdateModal} setVisible={setAddUpdateModal} askBeforeClose={true}>
                <AddUpdateUserGroup dataList={listUserGroup} setDataList={setListUserGroup} users={listUser} loadingUser={loadingUser} userGroupUpdate={userGroupUpdate} mode={mode} setUserGroupUpdate={setUserGroupUpdate} setAddUpdateModal={setAddUpdateModal} listDepartment={listDepartment} loadingDeparment={loadingDeparment} />
            </Modal>
        </MenuLayout>
    );
}


export default GrupoUsuarios;